import React, { useEffect } from "react";
import { useParams } from "react-router-dom";

import Layout from "src/components/layout/dashboard/Layout";
import PageHeader from "src/components/common/PageHeader";
import Card from "src/components/common/card/Card";
import { Badge } from "src/components/ui/Badge";
import { formatDateTime } from "src/utils/date-time";
import { currencyFormatter } from "src/utils/common";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as transactionActions from "src/redux/slices/transactionSlice";

const TransactionDetailsPage: React.FC = () => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const { transactionDetails } = useAppSelector((state) => state.transaction);

  useEffect(() => {
    if (params.transaction_id) {
      dispatch(
        transactionActions.getTransactionDetailsRequest(params.transaction_id)
      );
    }
  }, [params.transaction_id]);

  const transaction = transactionDetails?.data;

  return (
    <Layout
      type="admin"
      meta={{
        title: "Transaction Details | Invyntra",
        description: "View transaction details and status timeline.",
        keywords: "transaction, details, invyntra",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title={`#${transaction?.transactionId}` || "Transaction Details"}
          showBack
          breadcrumbData={[
            { label: "Home", path: "/admin/dashboard" },
            { label: "Transactions", path: "/admin/transaction/list" },
            {
              label: transaction?.transactionId
                ? `#${transaction?.transactionId}`
                : "Details",
              path: "",
            },
          ]}
        />

        <Card>
          <div className="space-y-3 text-sm text-gray-700">
            <div className="flex items-center gap-2">
              <span className="font-semibold">Transaction Id:</span>
              <span>#{transaction?.transactionId || "-"}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold">Amount:</span>
              <span>
                {transaction?.amount !== undefined
                  ? currencyFormatter(transaction.amount)
                  : "-"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold">Type:</span>
              <span>{transaction?.transactionType || "-"}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold">Payment Method:</span>
              <span>{transaction?.paymentMethod || "-"}</span>
            </div>
            {transaction?.paymentMethod === "online" && (
              <div className="flex items-center gap-2">
                <span className="font-semibold">Payment Mode:</span>
                <span>{transaction?.paymentMode || "-"}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="font-semibold">Status:</span>
              <Badge
                text={transaction?.status || ""}
                color={
                  transaction?.status === "success"
                    ? "green"
                    : transaction?.status === "processing"
                    ? "yellow"
                    : transaction?.status === "created"
                    ? "blue"
                    : "red"
                }
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold">Reference Id:</span>
              <span>{transaction?.referenceId || "-"}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold">Remarks:</span>
              <span>{transaction?.remarks || "-"}</span>
            </div>
            <div className="flex flex-col items-start gap-2">
              <span className="font-semibold">Data:</span>
              <pre className="border border-gray-300 bg-gray-100 rounded p-3 w-full">
                {JSON.stringify(JSON.parse(transaction?.data || "{}"), null, 4)}
              </pre>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold">Created On:</span>
              <span>
                {transaction?.createdAt
                  ? formatDateTime(
                      String(transaction.createdAt),
                      "DD/MM/YY HH:mm:ss"
                    )
                  : "-"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold">Updated On:</span>
              <span>
                {transaction?.updatedAt
                  ? formatDateTime(
                      String(transaction.updatedAt),
                      "DD/MM/YY HH:mm:ss"
                    )
                  : "-"}
              </span>
            </div>
          </div>
        </Card>

        <Card>
          <div className="text-sm text-gray-700">
            <div className="mb-3 text-base font-semibold">Status Timeline</div>
            {(transaction?.timeline || []).length === 0 ? (
              <div className="text-gray-500">No timeline entries.</div>
            ) : (
              <div className="space-y-2">
                {(transaction?.timeline || []).map((entry, index) => (
                  <div
                    key={`${entry.status}-${entry.at}-${index}`}
                    className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <Badge
                        text={entry.status}
                        color={
                          entry.status === "success"
                            ? "green"
                            : entry.status === "processing"
                            ? "yellow"
                            : entry.status === "created"
                            ? "blue"
                            : "red"
                        }
                      />
                      <span className="text-gray-600">
                        {entry.remarks || "-"}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {entry.at ? formatDateTime(String(entry.at)) : "-"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default TransactionDetailsPage;
